import type { Indicator } from "./Indicator";
import { ema, emaOfSeries, rsi, sma, stddev, trueRange } from "./math";

const closes = (d: { candles: { close: number }[] }) => d.candles.map((c) => c.close);

export const SMA: Indicator = {
  id: "sma", name: "Simple Moving Average", group: "overlay", defaults: { period: 20 },
  calculate: (d, s, color) => ({ overlays: [{ values: sma(closes(d), s.period), color }] }),
};

export const EMA: Indicator = {
  id: "ema", name: "Exponential Moving Average", group: "overlay", defaults: { period: 9 },
  calculate: (d, s, color) => ({ overlays: [{ values: ema(closes(d), s.period), color }] }),
};

export const Bollinger: Indicator = {
  id: "bb", name: "Bollinger Bands", group: "overlay", defaults: { period: 20, mult: 2 },
  calculate: (d, s, color) => {
    const c = closes(d);
    const mid = sma(c, s.period);
    const sd = stddev(c, s.period, mid);
    const upper = mid.map((m, i) => (m == null || sd[i] == null ? null : m + s.mult * sd[i]!));
    const lower = mid.map((m, i) => (m == null || sd[i] == null ? null : m - s.mult * sd[i]!));
    return { overlays: [{ values: mid, color, width: 1 }, { values: upper, color, dash: [3, 3] }, { values: lower, color, dash: [3, 3] }] };
  },
};

export const VWAP: Indicator = {
  id: "vwap", name: "VWAP (tick-volume)", group: "overlay", defaults: {},
  calculate: (d, _s, color) => {
    let pv = 0, vol = 0, day = -1;
    const values = d.candles.map((c) => {
      const cd = Math.floor(c.time / 86400);
      if (cd !== day) { day = cd; pv = 0; vol = 0; }
      const v = c.volume || 1;
      pv += ((c.high + c.low + c.close) / 3) * v; vol += v;
      return pv / vol;
    });
    return { overlays: [{ values, color }] };
  },
};

export const RSI: Indicator = {
  id: "rsi", name: "Relative Strength Index", group: "panel", defaults: { period: 14 },
  calculate: (d, s, color) => ({
    panel: { id: "rsi", title: `RSI ${s.period}`, lines: [{ values: rsi(closes(d), s.period), color }], range: [0, 100], levels: [30, 50, 70] },
  }),
};

export const MACD: Indicator = {
  id: "macd", name: "MACD", group: "panel", defaults: { fast: 12, slow: 26, signal: 9 },
  calculate: (d, s, color) => {
    const c = closes(d);
    const f = ema(c, s.fast), sl = ema(c, s.slow);
    const macd = f.map((v, i) => (v == null || sl[i] == null ? null : v - sl[i]!));
    const sig = emaOfSeries(macd, s.signal);
    const hist = macd.map((v, i) => (v == null || sig[i] == null ? null : v - sig[i]!));
    return {
      panel: {
        id: "macd", title: `MACD ${s.fast} ${s.slow} ${s.signal}`,
        lines: [{ values: macd, color }, { values: sig, color: "#FF6BD6" }],
        histogram: { values: hist }, levels: [0],
      },
    };
  },
};

export const ATR: Indicator = {
  id: "atr", name: "Average True Range", group: "panel", defaults: { period: 14 },
  calculate: (d, s, color) => ({
    panel: { id: "atr", title: `ATR ${s.period}`, lines: [{ values: ema(trueRange(d.candles), s.period), color }] },
  }),
};

export const CLASSIC_INDICATORS = [SMA, EMA, Bollinger, VWAP, RSI, MACD, ATR];
